import { createPlateEditor, createPlatePlugin } from 'platejs/react'
import type {
  AnyPlatePlugin,
  CreatePlateEditorOptions,
  PlateCorePlugin,
} from 'platejs/react'

import type {
  BufferEditor,
  BufferValue,
} from '~publish/legacy/editor/BufferEditor/types.plate'
import { createSafeClipboardPlugin } from '~publish/legacy/editor/plugins/clipboard/createSafeClipboardPlugin'
import { createEmojiSearchPlugin } from '~publish/legacy/editor/plugins/emoji'
import { createErrorReportingPlugin } from '~publish/legacy/editor/plugins/error-reporting/createErrorReportingPlugin'
import { createLinkPlugin } from '~publish/legacy/editor/plugins/link'
import { createTextSanitizationPlugin } from '~publish/legacy/editor/plugins/text-sanitization'

// Core `HtmlPlugin` (key 'html') registers a `text/html` parser that would
// otherwise deserialize pasted HTML, bypassing text-sanitization and mention
// paste guards (both only cover text/plain). Plate's core-plugin resolution
// swaps in a custom plugin whenever its key matches a core plugin's key, so
// this key-only stub suppresses HtmlPlugin's parser and keeps paste on the
// plain-text path — equivalent to v36's `disableCorePlugins.deserializeHtml`.
// Side effect: also removes `editor.api.html.deserialize` from composer
// editors (harmless today — the only caller is the separate Changelog editor).
const disableHtmlDeserialization = (): AnyPlatePlugin =>
  createPlatePlugin({ key: 'html' })

// Each converted plugin factory returns a distinctly-parameterized
// PlatePlugin<PluginConfig<...>> — TS treats those as mutually
// non-assignable (contravariant `unknown` positions in the plugin config,
// e.g. inject.targetPluginToInject), so a precisely-typed array of mixed
// plugins doesn't type-check. Stay loose here and narrow once, in the
// return's type guard below, rather than fighting that variance at every
// call site.
type CoreComposerPluginOptions = Readonly<{
  afterEmoji?: unknown[]
  afterTextSanitization?: unknown[]
  trailing?: unknown[]
}>

export const createCoreComposerPlugins = ({
  afterEmoji = [],
  afterTextSanitization = [],
  trailing = [],
}: CoreComposerPluginOptions = {}): PlateCorePlugin[] => {
  // Collect loosely and narrow with a type guard rather than a cast — it
  // also drops falsy entries: tests mock some factories to undefined, and
  // Plate throws on an undefined plugin — then cast to PlateCorePlugin, the
  // shape createPlateEditor's `plugins` option actually expects (see the
  // comment on CoreComposerPluginOptions above for why a precisely-typed
  // array doesn't work here). The guard only checks truthiness, not real
  // plugin shape — any truthy non-plugin here compiles and fails at
  // editor-creation time instead.
  const list: unknown[] = [
    disableHtmlDeserialization(),
    createEmojiSearchPlugin(),
    ...afterEmoji,
    createLinkPlugin(),
    createTextSanitizationPlugin(),
    ...afterTextSanitization,
    createSafeClipboardPlugin(),
    createErrorReportingPlugin(),
    ...trailing,
  ]
  return list.filter((plugin): plugin is AnyPlatePlugin =>
    Boolean(plugin),
  ) as PlateCorePlugin[]
}

export const createCoreComposerEditor = (
  options: CreatePlateEditorOptions<BufferValue>,
): BufferEditor =>
  createPlateEditor<BufferValue>({
    plugins: createCoreComposerPlugins(),
    ...options,
  }) as unknown as BufferEditor
